import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Badge,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  PrimarySparkLogo,
  type TanstackAppForm,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { NotFoundError, neverFunc, prettyPrintEnum } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { Suspense } from "react";
import {
  RiCheckboxBlankCircleFill,
  RiCheckboxCircleFill,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminFileImportTransactionFragment,
  CreateLedgerTransactionInput,
  CreateTransferAdminInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const maxDays = 14;

graphql(`
  fragment FileTransferImportOption on FundMovementTransactionTransferOption {
    id
    transfer {
      id
      requestedForDate
      description
      shortRef
      currencyAmount {
        amount
        currency
      }
      sourceAsset {
        id
        businessEntity {
          id
          name
          organisation {
            id
            name
            squareLogoUrl
          }
        }
        custodyAccount {
          id
        }
      }
      destinationAccount {
        ... on Asset {
          id
          custodyAccount {
            id
          }
        }
        ... on BeneficiaryAccount {
          id
        }
      }
    }
    matchedWithReference
  }

  fragment FileTransactionWestpacPaymentInstructionOption on FundMovementTransactionWestpacPaymentInstructionOption {
    id
    westpacPaymentInstruction {
      id
      shortRef
      status
      currencyAmount {
        amount
        currency
      }
      destinationCustodyAccount {
        id
        shortRef
        product {
          id
          internalName
          provider {
            id
            name
            logo {
              id
              url
            }
          }
        }
      }
      transfers {
        id
        shortRef
        sourceAsset {
          id
          businessEntity {
            id
            name
            organisation {
              id
              name
              squareLogoUrl
            }
          }
        }
        destinationAccount {
          ... on Asset {
            id
          }
          ... on BeneficiaryAccount {
            id
          }
        }
        currencyAmount {
          currency
          amount
        }
        requestedForDate
      }
    }
  }

  fragment FileTransactionFundUnitOrderOption on FundMovementTransactionFundUnitOrderOption {
    id
    fundUnitOrder {
      id
      shortRef
      currencyAmount {
        amount
        currency
      }
      custodyAccount {
        id
        shortRef
        product {
          id
          internalName
          name
          provider {
            id
            internalName
            name
            logo {
              id
              url
            }
          }
        }
      }
      transfers {
        id
        sourceAsset {
          id
        }
        destinationAccount {
          ... on Asset {
            id
          }
          ... on BeneficiaryAccount {
            id
          }
        }
        currencyAmount {
          currency
          amount
        }
      }
    }
  }

  fragment FileTransactionStripePayoutOption on FundMovementTransactionStripePayoutOption {
    id
    stripePayout {
      id
      shortRef
      organisation {
        id
        name
        squareLogoUrl
      }
      stripeId
      grossAmount {
        currency
        amount
      }
      depositAsset {
        id
        currency
      }
    }
  }

  fragment FileTransferNewImportOption on FundMovementTransactionTransferNewOption {
    id
    transferId
    asset {
      id
      name
    }
  }

  fragment FileDepositImportOption on FundMovementTransactionNewDepositOption {
    id
  }

  fragment FileAnnotationImportOption on FundMovementTransactionAnnotationOption {
    id
    annotation
  }

  fragment AdminFileImportTransaction on FileImportTransaction {
    id
    rowNumber
    importStatus
    description
    date
    amount
    custodyAccount {
      id
      currency
    }
    asset {
      id
      currency
      businessEntity {
        id
        name
        organisation {
          id
          name
          squareLogoUrl
        }
      }
      paymentIdentifiers {
        id
        ... on AuBsbPaymentIdentifier {
          id
          accountNumber
        }
      }
      name
    }
    matchedItem {
      ... on LedgerEntry {
        id
        shortRef
        transaction {
          id
        }
      }
      ... on FundUnitOrder {
        id
        shortRef
      }
      ... on WestpacPaymentInstruction {
        id
        shortRef
      }
    }
    fundMovementOptions {
      ...FileTransferNewImportOption
      ...FileDepositImportOption
      ...FileAnnotationImportOption
      ...FileTransferImportOption
      ...FileTransactionWestpacPaymentInstructionOption
      ...FileTransactionFundUnitOrderOption
      ...FileTransactionStripePayoutOption
    }
  }

  fragment AdminMacquarieFileImportFileDetails on MacquarieTransactionImportFile {
    id
    shortRef
    fileName
    uploadedAt
    createdByUser {
      id
      ...UserProfileUI
    }
    reconciliation(maxDays: $maxDays) {
      id
      maxDays
      totalTransactionCount
      transactions {
        id
        ...AdminFileImportTransaction
      }
    }
  }

  fragment AdminWestpacFileImportFileDetails on WestpacTransactionImportFile {
    id
    shortRef
    fileName
    uploadedAt
    createdByUser {
      id
      ...UserProfileUI
    }
    reconciliation(maxDays: $maxDays) {
      id
      maxDays
      totalTransactionCount
      transactions {
        id
        ...AdminFileImportTransaction
      }
    }
  }
`);

const formSchema = z.array(
  z.object({
    transactionId: z.string(),
    optionId: z.string(),
  }),
);

type FormValues = z.input<typeof formSchema>;

export const Route = createFileRoute(
  "/__auth/transaction-import_/$fileImportId",
)({
  head: () => opsHead("Transaction Import"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  if (!params.fileImportId) {
    throw new NotFoundError();
  }

  const gqlData = useSuspenseQuery(
    graphql(`
      query AdminTransactionFileImportDetails($id: ID!, $maxDays: Int!) {
        node(id: $id) {
          id
          ... on MacquarieTransactionImportFile {
            ...AdminMacquarieFileImportFileDetails
          }
          ... on WestpacTransactionImportFile {
            ...AdminWestpacFileImportFileDetails
          }
        }
      }
    `),
    {
      variables: {
        id: params.fileImportId,
        maxDays,
      },
    },
  );
  const file = gqlData.data.node;
  if (
    !file ||
    (file.__typename !== "MacquarieTransactionImportFile" &&
      file.__typename !== "WestpacTransactionImportFile")
  ) {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${prettyPrintImportFileType(file.__typename)} CSV file import "${file.fileName}"`,
  );

  const [createTransactions, { loading: createTransactionLoading, error }] =
    useMutation(
      graphql(`
        mutation TransactionImportCreateTransaction($input: CreateLedgerTransactionsInput!) {
          createLedgerTransactions(input: $input) {
            transactions {
              id
            }
          }
        }
      `),
    );

  const transactions = file.reconciliation?.transactions ?? [];
  const newTransactions = transactions.filter((t) => t.importStatus === "NEW");
  const matchedTransactionsCount = transactions.filter(
    (t) =>
      t.importStatus === "NEW" &&
      t.fundMovementOptions?.some(
        (v) =>
          v.__typename === "FundMovementTransactionTransferOption" &&
          v.matchedWithReference,
      ),
  ).length;

  const form = useAppForm({
    defaultValues: transactions.map((t) => ({
      transactionId: t.id,
      optionId:
        t.fundMovementOptions?.length && "id" in t.fundMovementOptions[0]
          ? t.fundMovementOptions[0].id
          : "",
    })) as FormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        throw new Error(`parsing form ${parsed.error}`);
      }

      const transfers: CreateTransferAdminInput[] = [];

      const ledgerTransactions: CreateLedgerTransactionInput[] =
        parsed.data.flatMap((d) => {
          const fileImportTransaction = transactions.find(
            (t) => t.id === d.transactionId,
          );
          if (!fileImportTransaction) {
            throw new Error(
              `expected file import transaction ${d.transactionId}`,
            );
          }
          if (fileImportTransaction.importStatus !== "NEW") {
            return [];
          }

          const direction = fileImportTransaction.amount.isPos()
            ? "CREDIT"
            : "DEBIT";

          const effectiveAt = DateTime.fromISO(fileImportTransaction.date, {
            zone: "Australia/Sydney",
          }).set({ minute: 0, hour: 17 });
          if (!effectiveAt.isValid) {
            throw new Error(
              `transaction date invalid ${fileImportTransaction.date}`,
            );
          }

          const unitId =
            fileImportTransaction.asset?.currency ??
            fileImportTransaction.custodyAccount?.currency;
          if (!unitId) {
            throw new Error("expected unitId to be defined");
          }

          const selectedOption =
            fileImportTransaction.fundMovementOptions?.find(
              (o) => "id" in o && o.id === d.optionId,
            );
          if (!selectedOption) {
            throw new Error(`selected option not found for ${d.transactionId}`);
          }
          let assetId = fileImportTransaction.asset?.id;
          if (fileImportTransaction.custodyAccount) {
            switch (selectedOption.__typename) {
              case "FundMovementTransactionTransferOption": {
                const destinationAccount =
                  selectedOption.transfer?.destinationAccount;

                if (
                  selectedOption.transfer?.sourceAsset?.custodyAccount?.id ===
                  fileImportTransaction.custodyAccount.id
                ) {
                  assetId = selectedOption.transfer?.sourceAsset?.id;
                } else if (
                  destinationAccount &&
                  "custodyAccount" in destinationAccount &&
                  destinationAccount.custodyAccount?.id ===
                    fileImportTransaction.custodyAccount.id
                ) {
                  assetId = destinationAccount.id;
                }
                break;
              }
              case "FundMovementTransactionWestpacPaymentInstructionOption": {
                if (direction !== "DEBIT") {
                  throw new Error(
                    `expected westpac payment instruction debit for ${d.transactionId}`,
                  );
                }
                const paymentInstruction =
                  selectedOption.westpacPaymentInstruction;
                if (!paymentInstruction) {
                  throw new Error(
                    `expected payment instruction for ${d.transactionId}`,
                  );
                }
                if (!paymentInstruction.transfers?.length) {
                  throw new Error(
                    `expected payment instruction transfers for ${d.transactionId}`,
                  );
                }

                const transferTxns =
                  paymentInstruction.transfers?.map((transfer) => {
                    if (!transfer.sourceAsset) {
                      throw new Error(
                        `expected transfer source asset for ${transfer.id}`,
                      );
                    }
                    if (!transfer.currencyAmount) {
                      throw new Error(
                        `expected transfer currency amount for ${transfer.id}`,
                      );
                    }

                    const isRedemption =
                      transfer.sourceAsset.__typename ===
                      "FixedIncomeFundAsset";

                    if (isRedemption) {
                      if (!transfer.destinationAccount) {
                        throw new Error(
                          `expected redemption transfer ${transfer.id} to have a destination account`,
                        );
                      }
                      if (
                        transfer.destinationAccount.__typename ===
                        "BeneficiaryAccount"
                      ) {
                        throw new Error(
                          `expected redemption transfer ${transfer.id} to have an asset destination but was beneficiary account`,
                        );
                      }

                      // westpac holding is the destination
                      return {
                        assetId: transfer.destinationAccount.id,
                        effectiveAt: effectiveAt.toJSDate(),
                        direction: "CREDIT" as const,
                        unitId: transfer.currencyAmount.currency,
                        normalAmount: transfer.currencyAmount.amount,
                        description: fileImportTransaction.description,
                        reference: fileImportTransaction.description,
                        transactionType: "TRANSFER_IN" as const,
                        transferId: transfer.id,
                        depositId: undefined,
                      };
                    } else {
                      if (
                        transfer.sourceAsset.__typename !== "BankAccountAsset"
                      ) {
                        throw new Error(
                          `expected subscription transfer ${transfer.id} source asset ${transfer.sourceAsset.id} to be BankAccountAsset`,
                        );
                      }
                      // westpac holding is the source
                      return {
                        assetId: transfer.sourceAsset.id,
                        effectiveAt: effectiveAt.toJSDate(),
                        direction: "DEBIT" as const,
                        unitId: transfer.currencyAmount.currency,
                        normalAmount: transfer.currencyAmount.amount,
                        description: fileImportTransaction.description,
                        reference: fileImportTransaction.description,
                        transactionType: "TRANSFER_OUT" as const,
                        transferId: transfer.id,
                        depositId: undefined,
                      };
                    }
                  }) ?? [];

                return transferTxns;
              }
              case "FundMovementTransactionFundUnitOrderOption": {
                if (direction !== "CREDIT") {
                  throw new Error(
                    `expected westpac payment instruction credit for ${d.transactionId}`,
                  );
                }
                const fundUnitOrder = selectedOption.fundUnitOrder;
                if (!fundUnitOrder) {
                  throw new Error(
                    `expected fundUnitOrder for ${d.transactionId}`,
                  );
                }
                if (!fundUnitOrder.transfers?.length) {
                  throw new Error(
                    `expected fundUnitOrder transfers for ${d.transactionId}`,
                  );
                }

                return fundUnitOrder.transfers.map((transfer) => {
                  if (!transfer.sourceAsset) {
                    throw new Error(
                      `expected transfer source asset for ${transfer.id}`,
                    );
                  }
                  if (!transfer.currencyAmount) {
                    throw new Error(
                      `expected transfer currency amount for ${transfer.id}`,
                    );
                  }

                  const isRedemption =
                    transfer.sourceAsset.__typename === "FixedIncomeFundAsset";

                  if (isRedemption) {
                    if (!transfer.destinationAccount) {
                      throw new Error(
                        `expected transfer destination account for ${transfer.id}`,
                      );
                    }
                    if (
                      transfer.destinationAccount.__typename ===
                      "BeneficiaryAccount"
                    ) {
                      throw new Error(
                        `expected fund unit order transfer ${transfer.id} to have asset destination but was beneficiary account`,
                      );
                    }

                    // westpac holding is the destination
                    return {
                      assetId: transfer.destinationAccount.id,
                      effectiveAt: effectiveAt.toJSDate(),
                      direction: "CREDIT" as const,
                      unitId: transfer.currencyAmount.currency,
                      normalAmount: transfer.currencyAmount.amount,
                      description: fileImportTransaction.description,
                      reference: fileImportTransaction.description,
                      transactionType: "TRANSFER_IN" as const,
                      transferId: transfer.id,
                      depositId: undefined,
                    };
                  } else {
                    if (
                      transfer.sourceAsset.__typename !== "BankAccountAsset"
                    ) {
                      throw new Error(
                        `expected subscription transfer ${transfer.id} source asset ${transfer.sourceAsset.id} to be BankAccountAsset`,
                      );
                    }
                    // westpac holding is the source
                    return {
                      assetId: transfer.sourceAsset.id,
                      effectiveAt: effectiveAt.toJSDate(),
                      direction: "DEBIT" as const,
                      unitId: transfer.currencyAmount.currency,
                      normalAmount: transfer.currencyAmount.amount,
                      description: fileImportTransaction.description,
                      reference: fileImportTransaction.description,
                      transactionType: "TRANSFER_OUT" as const,
                      transferId: transfer.id,
                      depositId: undefined,
                    };
                  }
                });
              }
              case "FundMovementTransactionStripePayoutOption": {
                if (direction !== "CREDIT") {
                  throw new Error(
                    `expected stripe payout credit for ${d.transactionId}`,
                  );
                }
                const stripePayout = selectedOption.stripePayout;
                if (!stripePayout) {
                  throw new Error(
                    `expected stripePayout for ${d.transactionId}`,
                  );
                }
                if (!stripePayout.depositAsset) {
                  throw new Error(
                    `expected deposit asset for ${stripePayout.id}`,
                  );
                }

                return {
                  assetId: stripePayout.depositAsset.id,
                  effectiveAt: effectiveAt.toJSDate(),
                  direction: "CREDIT",
                  unitId: stripePayout.depositAsset.currency,
                  normalAmount: fileImportTransaction.amount,
                  description: fileImportTransaction.description,
                  reference: stripePayout.stripeId,
                  transactionType: "STRIPE_PAYOUT",
                  stripePayoutQueueItemId: stripePayout.id,
                  transferId: undefined,
                  depositId: undefined,
                };
              }

              default: {
                throw new Error(
                  `${selectedOption.__typename} unimplemented for custody`,
                );
              }
            }
          }
          if (!assetId) {
            throw new Error(
              `expected asset for file import transaction ${d.transactionId}`,
            );
          }

          const newTransactionInput: CreateLedgerTransactionInput = {
            assetId: assetId,
            effectiveAt: effectiveAt.toJSDate(),
            direction,
            unitId,
            normalAmount: fileImportTransaction.amount.abs(),
            description: fileImportTransaction.description,
            reference: fileImportTransaction.description,
            transactionType: "TRANSFER_IN",
            transferId: undefined,
            depositId: undefined,
          };

          switch (selectedOption.__typename) {
            case "FundMovementTransactionTransferOption":
              const transfer = selectedOption.transfer;
              if (!transfer) {
                throw new Error(
                  `expected transfer in option for ${d.transactionId}`,
                );
              }
              newTransactionInput.transferId = transfer.id;
              newTransactionInput.transactionType =
                direction === "CREDIT" ? "TRANSFER_IN" : "TRANSFER_OUT";
              break;
            case "FundMovementTransactionAnnotationOption":
              if (selectedOption.annotation === "STRIPE_PAYOUT") {
                throw new Error(
                  "stripe payout annotation not supported on transaction import",
                );
              }
              newTransactionInput.transactionType = selectedOption.annotation;
              break;
            case "FundMovementTransactionNewDepositOption":
              newTransactionInput.transactionType = "DEPOSIT";
              break;
            case "FundMovementTransactionTransferNewOption":
              newTransactionInput.transactionType =
                direction === "CREDIT" ? "TRANSFER_IN" : "TRANSFER_OUT";
              newTransactionInput.transferId = selectedOption.transferId;

              if (direction === "DEBIT") {
                const sourceAsset = fileImportTransaction.asset;
                if (!sourceAsset) {
                  throw new Error(`expected asset for ${d.transactionId}`);
                }
                const destinationAsset = selectedOption.asset;
                if (!destinationAsset) {
                  throw new Error(
                    `expected asset for selected option ${d.transactionId}`,
                  );
                }
                const organisationId =
                  sourceAsset.businessEntity?.organisation?.id;
                if (!organisationId) {
                  throw new Error(
                    `expected organisation for ${d.transactionId}`,
                  );
                }

                transfers.push({
                  transferId: selectedOption.transferId,
                  amount: fileImportTransaction.amount.abs(),
                  unit: "AUD",
                  side: "SOURCE",
                  date: effectiveAt.toJSDate(),
                  description: fileImportTransaction.description,
                  sourceAssetId: sourceAsset.id,
                  destinationAccount: { assetId: destinationAsset.id },
                  organisationId,
                  bypassApprovalPolicyOption: "TRANSFER_CREATED_RETROACTIVELY",
                });
              }
              break;
            case "FundMovementTransactionWestpacPaymentInstructionOption":
            case "FundMovementTransactionFundUnitOrderOption":
            case "FundMovementTransactionStripePayoutOption":
              throw new Error("unimplemented");
            default:
              throw neverFunc(selectedOption);
          }

          return [newTransactionInput];
        });

      await createTransactions({
        variables: {
          input: {
            ledgerTransactions,
            transfers,
            fileImportId: params.fileImportId,
          },
        },
        onCompleted: () => {
          void navigate({ to: "/transactions-queue/ready-today" });
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors.map((e) => e.message).join(", ") ??
            "Server error";
          toast({
            kind: "error",
            autoHide: false,
            title: "Error creating transactions",
            description: errorMessage,
          });
        },
      });
    },
  });

  const formValid = useStore(form.store, (state) => {
    const parsed = formSchema.safeParse(state.values);
    return parsed.success;
  });

  return (
    <div className="flex flex-col gap-4 p-12">
      <AdminPageHeader
        title={`${prettyPrintImportFileType(file.__typename)} CSV file import "${file.fileName}"`}
      />

      {error && (
        <Alert title="Import Error" kind="error" description={error.message} />
      )}

      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-end justify-between">
              <div>
                <div className="flex space-x-1">
                  <div className="font-semibold">
                    {file.reconciliation?.totalTransactionCount}
                  </div>
                  <div>transactions found in CSV</div>
                </div>
                <div className="flex space-x-1">
                  <div className="font-semibold">{transactions.length}</div>
                  <div>transactions found in the last {maxDays} days</div>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="font-semibold">
                    {matchedTransactionsCount}
                  </div>
                  <div>transactions auto-reconciled by Primary AI</div>
                  <div>
                    <PrimarySparkLogo
                      className="h-4 w-4 shrink-0"
                      variant={
                        matchedTransactionsCount > 0 ? "default" : "secondary"
                      }
                    />
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                type="submit"
                disabled={
                  createTransactionLoading ||
                  !newTransactions.length ||
                  !formValid
                }
                loading={createTransactionLoading}
              >
                Import {newTransactions.length} transactions
              </Button>
            </div>
            <DataTable
              data={transactions}
              columns={createColumns(form)}
              pageSize={100_000}
              hidePagination
              className="overflow-hidden border"
              showCsvExport={false}
            />
          </div>
        </form>
      </form.AppForm>
    </div>
  );
}

const createColumns = (
  form: TanstackAppForm<FormValues>,
): ColumnDef<AdminFileImportTransactionFragment>[] => [
  {
    id: "date",
    accessorFn: (row) => row.date,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transaction date" />
    ),
    cell: ({ row }) => {
      return <DateUI variant="DATE" date={new Date(row.original.date)} />;
    },
  },
  {
    id: "asset",
    accessorFn: (row) => row.asset,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset" />
    ),
    cell: ({ row }) => {
      if (!row.original.asset) {
        return "-";
      }
      const organisation = row.original.asset.businessEntity?.organisation;
      if (!organisation) {
        return "-";
      }
      const paymentIdentifier = row.original.asset.paymentIdentifiers?.find(
        (p) => p.__typename === "AuBsbPaymentIdentifier",
      );
      return (
        <div className="flex w-fit items-center gap-2">
          {organisation?.squareLogoUrl && (
            <img
              className="size-8 rounded-xs"
              src={organisation.squareLogoUrl}
              alt="Organisation logo"
            />
          )}
          <div className="flex flex-col">
            <Txt>
              {organisation?.name} - {row.original.asset.businessEntity?.name}
            </Txt>
            <Txt>
              {row.original.asset.name} - {
                paymentIdentifier?.accountNumber
              }{" "}
            </Txt>
          </div>
        </div>
      );
    },
  },
  {
    id: "description",
    accessorFn: (row) => row.description,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          <div>{row.original.description}</div>
          <div className="font-mono text-xs">
            Row {row.original.rowNumber + 1}
          </div>
        </div>
      );
    },
  },
  {
    id: "amount",
    accessorFn: (row) => row.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex gap-2">
          {row.original.amount.isPos() ? "CR" : "DR"}
          <CurrencyAmountUI
            amount={row.original.amount.abs()}
            currency="AUD"
            hideCurrency
          />
        </div>
      );
    },
  },
  {
    id: "purpose",
    accessorFn: (row) => row.fundMovementOptions,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Purpose"
        className="min-w-[500px]"
      />
    ),
    cell: ({ row }) => {
      switch (row.original.importStatus) {
        case "EXISTING": {
          if (!row.original.matchedItem) {
            return <p className="text-sm text-warning">No matched item</p>;
          }

          switch (row.original.matchedItem.__typename) {
            case "LedgerEntry":
              return (
                <Link
                  to={`/ledger/transactions/${row.original.matchedItem.transaction?.id}`}
                  target="_blank"
                >
                  <div className="flex items-center gap-3 pr-3">
                    <RiCheckboxCircleFill className="text-lg text-success" />
                    <div className="flex flex-col gap-1 text-sm text-tertiary">
                      Imported Ledger entry #{row.original.matchedItem.shortRef}
                    </div>
                  </div>
                </Link>
              );
            case "FundUnitOrder":
              return (
                <div className="flex items-center gap-3 pr-3">
                  <RiCheckboxCircleFill className="text-lg text-success" />
                  <div className="flex flex-col gap-1 text-sm text-tertiary">
                    Imported fund unit order #
                    {row.original.matchedItem?.shortRef}
                  </div>
                </div>
              );
            case "WestpacPaymentInstruction":
              return (
                <div className="flex items-center gap-3 pr-3">
                  <RiCheckboxCircleFill className="text-lg text-success" />
                  <div className="flex flex-col gap-1 text-sm text-tertiary">
                    Imported Westpac payment instruction #
                    {row.original.matchedItem?.shortRef}
                  </div>
                </div>
              );
            default:
              throw neverFunc(row.original.matchedItem);
          }
        }
        case "UNKNOWN":
          return (
            <div className="flex space-x-1">
              <RiCheckboxBlankCircleFill className="text-danger text-lg" />
              <div>Failed to reconcile</div>
            </div>
          );
        case "NEW":
          const options = row.original.fundMovementOptions;
          if (!options) {
            throw new Error(
              `expected transaction options for ${row.original.id}`,
            );
          }

          return (
            <form.AppField
              name={`[${row.index}].optionId` as `[${number}].optionId`}
            >
              {(field) => (
                <field.SelectField
                  placeholder="Select an option"
                  errorVisibilityMode="onBlur"
                >
                  {options.map((opt) => {
                    if (!("id" in opt)) {
                      throw new Error("expected option to have id");
                    }
                    return (
                      <field.SelectItem key={opt.id} value={opt.id}>
                        <div className="flex flex-grow">
                          {(() => {
                            switch (opt.__typename) {
                              case "FundMovementTransactionNewDepositOption":
                                return (
                                  <div className="font-semibold">
                                    New unplanned deposit
                                  </div>
                                );
                              case "FundMovementTransactionTransferOption":
                                if (!opt.transfer) {
                                  return null;
                                }
                                const actionLabel =
                                  opt.transfer.destinationAccount
                                    ?.__typename === "BeneficiaryAccount"
                                    ? "Payout"
                                    : "Transfer";

                                return (
                                  <div className="flex flex-grow flex-col">
                                    <div className="flex w-full justify-between">
                                      <div className="flex items-center gap-2 font-semibold">
                                        {opt.matchedWithReference && (
                                          <RiCheckboxCircleFill className="text-lg text-success" />
                                        )}
                                        #{opt.transfer.shortRef} {actionLabel}
                                      </div>

                                      {row.original.custodyAccount && (
                                        <div className="flex flex-row items-center gap-2">
                                          <Icon
                                            imageUrl={
                                              opt.transfer.sourceAsset
                                                ?.businessEntity?.organisation
                                                ?.squareLogoUrl ?? undefined
                                            }
                                          />
                                          <div className="flex flex-col overflow-hidden">
                                            <p className="max-w-32 truncate text-sm">
                                              {
                                                opt.transfer.sourceAsset
                                                  ?.businessEntity?.organisation
                                                  ?.name
                                              }
                                            </p>
                                            <p className="max-w-32 truncate text-secondary text-xs">
                                              {
                                                opt.transfer.sourceAsset
                                                  ?.businessEntity?.name
                                              }
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      <div>
                                        {opt.transfer.currencyAmount && (
                                          <CurrencyAmountUI
                                            amount={
                                              opt.transfer.currencyAmount.amount
                                            }
                                            currency={
                                              opt.transfer.currencyAmount
                                                .currency
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex w-full justify-between">
                                      <DateUI
                                        date={opt.transfer.requestedForDate}
                                        variant="DATE"
                                      />
                                      {opt.matchedWithReference && (
                                        <Badge variant="outline">
                                          Rule
                                          <PrimarySparkLogo className="ml-1 inline-block h-3 w-3 align-middle opacity-80" />
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              case "FundMovementTransactionAnnotationOption":
                                return (
                                  <div className="font-semibold">
                                    {prettyPrintEnum(opt.annotation)}
                                  </div>
                                );
                              case "FundMovementTransactionTransferNewOption":
                                return (
                                  <div className="font-semibold">
                                    #{opt.transferId.slice(-6).toUpperCase()}{" "}
                                    New transfer{" "}
                                    {row.original.amount.isPos()
                                      ? "from"
                                      : "to"}{" "}
                                    {opt.asset?.name}
                                  </div>
                                );
                              case "FundMovementTransactionWestpacPaymentInstructionOption":
                                if (!opt.westpacPaymentInstruction) {
                                  return null;
                                }

                                const transfers =
                                  opt.westpacPaymentInstruction.transfers ?? [];
                                const totalFundMovements = transfers.length;

                                if (totalFundMovements === 1) {
                                  const transfer = transfers[0];
                                  if (transfer) {
                                    return (
                                      <div className="flex flex-grow flex-col">
                                        <div className="flex w-full justify-between">
                                          <div className="flex items-center gap-2 font-semibold">
                                            #{transfer.shortRef} Transfer
                                          </div>

                                          {row.original.custodyAccount && (
                                            <div className="flex flex-row items-center gap-2">
                                              <Icon
                                                imageUrl={
                                                  transfer.sourceAsset
                                                    ?.businessEntity
                                                    ?.organisation
                                                    ?.squareLogoUrl ?? undefined
                                                }
                                              />
                                              <div className="flex flex-col overflow-hidden">
                                                <p className="max-w-32 truncate text-sm">
                                                  {
                                                    transfer.sourceAsset
                                                      ?.businessEntity
                                                      ?.organisation?.name
                                                  }
                                                </p>
                                                <p className="max-w-32 truncate text-secondary text-xs">
                                                  {
                                                    transfer.sourceAsset
                                                      ?.businessEntity?.name
                                                  }
                                                </p>
                                              </div>
                                            </div>
                                          )}

                                          <div>
                                            {transfer.currencyAmount && (
                                              <CurrencyAmountUI
                                                amount={
                                                  transfer.currencyAmount.amount
                                                }
                                                currency={
                                                  transfer.currencyAmount
                                                    .currency
                                                }
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex w-full justify-between">
                                          <DateUI
                                            date={transfer.requestedForDate}
                                            variant="DATE"
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                }

                                return (
                                  <div className="flex flex-grow flex-col gap-2">
                                    <div className="flex w-full justify-between">
                                      <div className="font-semibold">
                                        #
                                        {opt.westpacPaymentInstruction.shortRef}{" "}
                                        Payment instruction
                                      </div>
                                      <div>
                                        <CurrencyAmountUI
                                          amount={
                                            opt.westpacPaymentInstruction
                                              .currencyAmount.amount
                                          }
                                          currency={
                                            opt.westpacPaymentInstruction
                                              .currencyAmount.currency
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <Icon
                                          imageUrl={
                                            opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.product?.provider?.logo?.url ??
                                            undefined
                                          }
                                        />
                                        <div className="flex min-w-0 flex-col">
                                          <div className="font-semibold">
                                            {totalFundMovements} linked fund
                                            movement
                                            {totalFundMovements === 1
                                              ? ""
                                              : "s"}
                                          </div>
                                          <div className="truncate text-secondary text-xs">
                                            {opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.shortRef ??
                                              "Destination custody account"}
                                            {" · "}
                                            {opt.westpacPaymentInstruction
                                              .destinationCustodyAccount
                                              ?.product?.internalName ??
                                              "Destination account"}
                                            {totalFundMovements > 1
                                              ? " + more"
                                              : ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right text-secondary text-xs">
                                        Creates one transfer out transaction per
                                        linked transfer
                                      </div>
                                    </div>
                                  </div>
                                );
                              case "FundMovementTransactionFundUnitOrderOption":
                                if (!opt.fundUnitOrder) {
                                  return null;
                                }

                                return (
                                  <div className="flex flex-grow flex-col gap-2">
                                    <div className="flex w-full justify-between">
                                      <div className="font-semibold">
                                        #{opt.fundUnitOrder.shortRef} Fund Unit
                                        Order
                                      </div>
                                      <div>
                                        <CurrencyAmountUI
                                          {...opt.fundUnitOrder.currencyAmount}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <Icon
                                          imageUrl={
                                            opt.fundUnitOrder.custodyAccount
                                              ?.product?.provider?.logo?.url ??
                                            undefined
                                          }
                                        />
                                        <div className="flex min-w-0 flex-col">
                                          <div className="font-semibold">
                                            {
                                              opt.fundUnitOrder.transfers
                                                ?.length
                                            }{" "}
                                            linked transfer
                                            {opt.fundUnitOrder.transfers
                                              ?.length === 1
                                              ? ""
                                              : "s"}
                                          </div>
                                          <div className="truncate text-secondary text-xs">
                                            {opt.fundUnitOrder.custodyAccount
                                              ?.shortRef ??
                                              "Destination custody account"}
                                            {" · "}
                                            {opt.fundUnitOrder.custodyAccount
                                              ?.product?.internalName ??
                                              "Destination account"}
                                            {opt.fundUnitOrder.transfers &&
                                            opt.fundUnitOrder.transfers.length >
                                              1
                                              ? " + more"
                                              : ""}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right text-secondary text-xs">
                                        Creates one transfer out transaction per
                                        linked transfer
                                      </div>
                                    </div>
                                  </div>
                                );
                              case "FundMovementTransactionStripePayoutOption": {
                                if (!opt.stripePayout) {
                                  return null;
                                }
                                return (
                                  <div className="flex w-full flex-row items-center gap-4">
                                    <img
                                      className="size-8 rounded-xs"
                                      src={
                                        opt.stripePayout.organisation
                                          ?.squareLogoUrl ?? undefined
                                      }
                                      alt="Organisation logo"
                                    />
                                    <div className="flex flex-grow flex-col">
                                      <div className="flex w-full justify-between">
                                        <div className="flex items-center gap-2 font-semibold">
                                          #{opt.stripePayout.shortRef} Stripe
                                          Payout
                                        </div>
                                      </div>
                                      <div className="flex w-full justify-between">
                                        <div>
                                          <DateUI
                                            date={new Date(row.original.date)}
                                            variant="DATE"
                                          />
                                          {" · "}
                                          {opt.stripePayout.organisation?.name}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              default:
                                throw neverFunc(opt);
                            }
                          })()}
                        </div>
                      </field.SelectItem>
                    );
                  })}
                </field.SelectField>
              )}
            </form.AppField>
          );
        default:
          throw new Error("expected import status");
      }
    },
  },
];

function prettyPrintImportFileType(
  fileType: "MacquarieTransactionImportFile" | "WestpacTransactionImportFile",
): string {
  switch (fileType) {
    case "MacquarieTransactionImportFile":
      return "Macquarie";
    case "WestpacTransactionImportFile":
      return "Westpac";
    default:
      return neverFunc(fileType);
  }
}
